// The cards of the environment page: limits against the organisation's quota, workspaces,
// network and VPN, and the egress allowlist.
import { Check, GlobeHemisphereWest, PlugsConnected, Warning } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";

import type { Environment, Organisation } from "../../api/client";
import { useT } from "../../i18n";
import { WidgetCard } from "../../ui/Card";
import { Meter } from "../../ui/Meter";
import { EmptyState, ErrorState, Loading } from "../../ui/states";
import { Mono } from "../../ui/status";
import { environmentWorkspacesQuery, type Network, networkQuery } from "../api";
import { gibCount } from "../format";
import { WorkspaceCard } from "./WorkspaceCard";

export function LimitsCard({ env, org }: { env: Environment; org: Organisation | undefined }) {
  const { workloads } = useT();
  const t = workloads.environment.limits;
  return (
    <WidgetCard title={t.title} description={t.description}>
      {org ? (
        <div className="space-y-5">
          <Meter label={t.cpu} used={env.limits_cpu} total={org.quota_cpu} unit={workloads.cores} detail={t.cpuDetail(org.name)} />
          <Meter label={t.memory} used={gibCount(env.limits_memory_bytes)} total={gibCount(org.quota_memory_bytes)} unit="GiB" detail={t.memoryDetail(org.name)} />
        </div>
      ) : (
        <p className="m-0 text-[13.5px] text-gray-700">
          {env.limits_cpu} {workloads.cores} · {gibCount(env.limits_memory_bytes)} GiB
        </p>
      )}
    </WidgetCard>
  );
}

export function WorkspacesCard({ env }: { env: Environment }) {
  const t = useT().workloads.environment.workspaces;
  const workspaces = useQuery(environmentWorkspacesQuery(env.id));
  return (
    <WidgetCard title={t.title} description={t.description}>
      {workspaces.isPending ? <Loading /> : null}
      {workspaces.isError ? <ErrorState message={workspaces.error.message} /> : null}
      {workspaces.data?.length === 0 ? (
        <EmptyState compact title={t.emptyTitle}>
          {t.emptyBody}
        </EmptyState>
      ) : null}
      <div className="space-y-3">
        {workspaces.data?.map((w) => (
          <WorkspaceCard key={w.id} workspace={w} />
        ))}
      </div>
    </WidgetCard>
  );
}

function VpnDetail({ network }: { network: Network }) {
  const t = useT().workloads.environment.network;
  return (
    <div className="text-gray-500">
      {t.profile(network.vpn_type, network.vpn_required)}
      {network.split_tunnel_routes.length > 0 ? (
        <>
          {" "}
          {t.routes} <Mono>{network.split_tunnel_routes.join(", ")}</Mono>
        </>
      ) : null}
      {network.dns_servers.length > 0 ? (
        <>
          {" "}
          · {t.dns} <Mono>{network.dns_servers.join(", ")}</Mono>
        </>
      ) : null}
    </div>
  );
}

export function NetworkCard({ env }: { env: Environment }) {
  const { common, workloads } = useT();
  const t = workloads.environment.network;
  const network = useQuery({ ...networkQuery(env.network_id ?? ""), enabled: env.network_id != null });
  return (
    <WidgetCard title={t.title} description={t.description}>
      <ul className="m-0 list-none space-y-3 p-0 text-[13.5px]">
        <li className="flex items-start gap-3">
          <PlugsConnected weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
          <div className="min-w-0">
            <div className="font-medium text-gray-900">{t.vpn}</div>
            {env.network_id == null ? (
              <div className="text-gray-500">{t.noVpn}</div>
            ) : network.data ? (
              <VpnDetail network={network.data} />
            ) : (
              <div className="text-gray-500">{network.isError ? network.error.message : common.states.loading}</div>
            )}
          </div>
        </li>
        <li className="flex items-start gap-3">
          <GlobeHemisphereWest weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
          <div>
            <div className="font-medium text-gray-900">{t.egress}</div>
            <div className="text-gray-500">{t.egressBody}</div>
          </div>
        </li>
        <li className="flex items-start gap-3 rounded-lg bg-orange-lighter/60 p-3 text-orange-dark">
          <Warning weight="duotone" className="mt-0.5 h-5 w-5 shrink-0" />
          <span className="text-[13px]">{t.overlap}</span>
        </li>
      </ul>
    </WidgetCard>
  );
}

export function EgressCard({ env }: { env: Environment }) {
  const t = useT().workloads.environment.egress;
  const n = env.egress_allowlist.length;
  return (
    <WidgetCard title={t.title} description={t.description(n)}>
      {n === 0 ? <p className="m-0 text-[13px] text-gray-500">{t.nothing}</p> : null}
      <div className="flex flex-wrap gap-2">
        {env.egress_allowlist.map((d) => (
          <span key={d} className="inline-flex items-center gap-1.5 rounded-lg border border-muted bg-gray-50 px-2.5 py-1.5 font-mono text-[12px] text-gray-700 dark:bg-gray-100/50">
            <Check weight="bold" className="h-3 w-3 text-green" />
            {d}
          </span>
        ))}
      </div>
    </WidgetCard>
  );
}
